import { useT } from '../i18n/index.ts';
import { LocaleSwitcher } from '../i18n/LocaleSwitcher.tsx';
import { CONSOLE_LOGIN_URL, CONSOLE_REGISTER_URL } from '../config/endpoints.ts';
import { Wordmark } from './Wordmark.tsx';

function currentPathname(): string {
  if (typeof window === 'undefined') {
    return '/';
  }
  return window.location.pathname.replace(/\/+$/, '') || '/';
}

function navLinkClass(active: boolean): string {
  const base = 'relative transition-colors hover:text-ink';
  if (!active) {
    return base;
  }
  return `${base} font-semibold text-ink after:absolute after:-bottom-2 after:left-0 after:h-px after:w-full after:bg-claude`;
}

/**
 * Sticky page header.
 *
 * 64px tall, `bg-linen/85 backdrop-blur-md` at every scroll position
 * with a `ghost-b` underline — DESIGN.md §6.1. First cut stays zero-JS
 * on the header; a scroll-driven opacity shift can land in a later pass
 * if visual design asks for it.
 *
 * Layout left→right: wordmark, primary nav (collapsed below `md`),
 * locale switcher, "Sign in" text link, "Get started" filled pill.
 */
export function Header(): JSX.Element {
  const t = useT();
  const pathname = currentPathname();
  const isPricingActive = pathname.endsWith('/pricing');
  const isStatusActive = pathname.endsWith('/status');

  return (
    <header className="ghost-b sticky top-0 z-50 bg-linen/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <a
          href="/"
          aria-label="nadoutong"
          data-testid="header-wordmark"
          className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-claude focus-visible:ring-offset-2 focus-visible:ring-offset-linen"
        >
          <Wordmark />
        </a>

        <nav className="flex min-w-0 items-center gap-3 lg:gap-5">
          <ul className="hidden items-center gap-6 text-sm text-ink-soft lg:flex xl:gap-9">
            <li>
              <a
                href="/#docs"
                className={navLinkClass(false)}
                data-testid="header-nav-docs"
              >
                {t('header.nav.docs')}
              </a>
            </li>
            <li>
              <a
                href="/pricing"
                aria-current={isPricingActive ? 'page' : undefined}
                className={navLinkClass(isPricingActive)}
                data-testid="header-nav-pricing"
              >
                {t('header.nav.pricing')}
              </a>
            </li>
            <li>
              <a
                href="/status"
                aria-current={isStatusActive ? 'page' : undefined}
                className={navLinkClass(isStatusActive)}
                data-testid="header-nav-status"
              >
                {t('header.nav.status')}
              </a>
            </li>
            <li>
              <a
                href="/#docs/tools"
                className={navLinkClass(false)}
                data-testid="header-nav-tools"
              >
                {t('header.nav.tools')}
              </a>
            </li>
          </ul>
          <LocaleSwitcher />
          <a
            href={CONSOLE_LOGIN_URL}
            className="hidden rounded-full px-3 py-2 text-sm text-ink-soft transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-claude focus-visible:ring-offset-2 focus-visible:ring-offset-linen lg:inline-flex"
            data-testid="header-cta-sign-in"
          >
            {t('header.cta.sign_in')}
          </a>
          <a
            href={CONSOLE_REGISTER_URL}
            className="inline-flex items-center rounded-full bg-ink px-4 py-2 text-[13.5px] font-medium text-linen transition-colors hover:bg-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-claude focus-visible:ring-offset-2 focus-visible:ring-offset-linen"
            data-testid="header-cta-get-started"
          >
            {t('header.cta.get_started')}
          </a>
        </nav>
      </div>
    </header>
  );
}
